import {
  type ConfigurationValidationError,
  isTemplateDependencyFunctionValidationError,
} from '@festina/configuration-general-types/configuration'
import { useCallback } from 'react'

import { useTemplateOperationKeyFromPath } from '../components/templateDetails/content/useTemplateOperationKeyFromPath'
import { functionExpressionPath } from '../components/templateFieldsTable/fieldsHelpers'
import {
  type ConfigurationTemplateFunctionValue,
  isConfigurationFunction,
  isSubTemplatedField,
  isSubTemplatedFieldValue,
  type SubTemplateFieldValue,
  type TemplateField,
  ValueSetupRole,
} from '../types/templateModel'
import { isTemplateDependencyValidationError } from '../uiModels/types'

/**
 * Utility hook used to determine if an error belongs to field. This has been put inside a hook since
 * we'll need to read from the template context and the configuration context once we want to support error
 * mappings being shown in flattened views.
 */
export const useIsErrorForField = () => {
  const getTemplateOperationKeyFromPath = useTemplateOperationKeyFromPath()

  return useCallback(
    (
      field:
        | TemplateField
        | ConfigurationTemplateFunctionValue
        | SubTemplateFieldValue,
      error: ConfigurationValidationError,
    ) => {
      if (!isTemplateDependencyValidationError(error)) {
        return false
      }

      const fieldOrigin = field.origin

      if (!fieldOrigin) {
        return false
      }

      const operationKey = getTemplateOperationKeyFromPath(fieldOrigin)

      if (isConfigurationFunction(field)) {
        return (
          isTemplateDependencyFunctionValidationError(error) &&
          error.operationKey === operationKey &&
          error.path === functionExpressionPath(fieldOrigin) &&
          error.source === field.function?.fplusSource
        )
      }

      if (isSubTemplatedFieldValue(field)) {
        return (
          error.operationKey === operationKey &&
          (error.path === `${fieldOrigin}.selectable` ||
            error.path === `${fieldOrigin}.defaultSelected`)
        )
      }

      if (isSubTemplatedField(field)) {
        const isSingleSubClassError =
          field.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE &&
          field.value?.variants?.at(0) &&
          (error.path ===
            `${fieldOrigin}{${field.value.variants[0].subTemplateIdentifier}}.selectable` ||
            error.path ===
              `${fieldOrigin}{${field.value.variants[0].subTemplateIdentifier}}.defaultSelected`)
        const isSubtemplatedFieldError =
          error.path === fieldOrigin ||
          error.path === `${fieldOrigin}.cardinality` ||
          error.path === `${fieldOrigin}.valueConfig` ||
          error.path?.startsWith(`${fieldOrigin}.validations`)

        return (
          error.operationKey === operationKey &&
          (isSingleSubClassError || isSubtemplatedFieldError)
        )
      }

      return (
        error.operationKey === operationKey &&
        (error.path ?? '').startsWith(fieldOrigin)
      )
    },
    [getTemplateOperationKeyFromPath],
  )
}
